import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SelectableCard,
  Textarea,
} from '@h2hspark/ui';
import { Copy, ExternalLink } from 'lucide-react';
import * as React from 'react';

import { useReturnFocusToOpener } from '../../member/use-overlay-focus-return';

import { useCopyFollowUp, useMarkFollowUpDone, useSaveFollowUpDraft } from './api';
import {
  CHANNELS_LABEL,
  COPIED_LABEL,
  COPIED_NOTICE,
  COPY_OPENER_LABEL,
  composerTitle,
  DRAFT_LABEL,
  MARK_DONE_DESCRIPTION,
  MARK_DONE_LABEL,
  NO_CHANNEL_NOTICE,
  OPENER_PICKER_LABEL,
  roleAndCompany,
  START_FROM_SCRATCH_LABEL,
} from './constants';
import type { FollowUpItem } from './types';

/**
 * The composer (`MEM-037` AC-2, AC-3, AC-4; `MEM-038` AC-1, AC-2, AC-3, AC-6).
 *
 * ## Transcribed from `Member App.dc.html`, the follow-up composer overlay
 *
 * A full-screen overlay headed **"Follow up with {name}"** with the person's role beneath it
 * (`MEM-037` AC-2 — the prototype's string is the AC's string), the section label *"Pick a message
 * to start from"* over the generated openers as selectable cards, a dashed **Start from scratch**
 * option, then *"Your message"* over an editable textarea, and a sticky primary action.
 *
 * ## This dialog copies and does not send, and that is now a scope line rather than an absence
 *
 * The prototype's primary action is **`Send message`**, and behind it the handoff has a Messages
 * inbox, a threaded conversation view and canned inbound replies. ADR 032 put all of it in Phase 2
 * and `MEM-038` AC-1 replaced the action with **Copy opener**; ADR 052 supersedes ADR 032 and
 * `MEM-054` builds all three, so that argument no longer holds anything up.
 *
 * What is true today: **Send lives on the opener card** (`follow-up-detail-page.tsx`,
 * `MEM-054` AC-4 — *"Send on a follow-up opener delivers the edited draft as an ordinary message
 * on the pair's thread"*), and this overlay is the **picker**, whose subject is choosing and
 * editing a draft rather than dispatching it. So it still ends in Copy, and nothing on this screen
 * sends anything; `useCopyFollowUp` records the member's own assertion that they handled it
 * (AC-3). Adding a second Send here is a design question for this overlay, not a leftover.
 * The prototype has no Copy affordance at all, so this and the channel list below are written
 * rather than matched.
 *
 * ## The draft is theirs (`MEM-037` AC-4)
 *
 * `draft` is seeded from the stored draft when there is one and from the first opener when there
 * is not, and **the picker only overwrites it on an explicit tap**. Reopening the composer does not
 * re-seed, because `item.draft` is what the API returns after a save — that is the whole of AC-4,
 * and it is why `openers` and `draft` are separate fields all the way down.
 */
export function FollowUpComposerDialog(props: {
  readonly eventId: string;
  readonly item: FollowUpItem;
  /**
   * What the opener card's own textarea currently holds, when the composer was opened from one.
   *
   * Without it the overlay would seed from `item.draft` and silently discard whatever the member
   * had typed on the card but not yet saved — `MEM-037` AC-4's *"the member's edits are theirs"*
   * broken by the act of looking at the other openers.
   */
  readonly initialDraft?: string;
  /** Hands the chosen or edited text back to the card, so the two never show different messages. */
  readonly onDraftChange?: (draft: string) => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const { eventId, item, initialDraft, onDraftChange, onClose } = props;
  const copy = useCopyFollowUp();
  const markDone = useMarkFollowUpDone();
  const saveDraft = useSaveFollowUpDraft();

  // The member's own text wins over the generated set whenever there is any.
  const [draft, setDraftState] = React.useState<string>(
    initialDraft ?? item.draft ?? item.openers[0] ?? '',
  );
  const setDraft = React.useCallback(
    (next: string) => {
      setDraftState(next);
      onDraftChange?.(next);
    },
    [onDraftChange],
  );
  const [copied, setCopied] = React.useState(false);
  const subtitle = roleAndCompany(item);

  /**
   * `MEM-038` AC-3 — the clipboard first, the state second.
   *
   * The state change follows a successful write to the clipboard rather than accompanying the
   * click, because "copied" is the member's assertion about something that happened. If the
   * clipboard refuses — an insecure context, a permission — the item stays `ready` and the member
   * still has the text in front of them to select by hand.
   */
  const onCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(draft);
    setCopied(true);
    if (draft !== item.draft) {
      await saveDraft.mutateAsync({ eventId, attendeeRef: item.attendeeRef, draft });
    }
    await copy.mutateAsync({ eventId, attendeeRef: item.attendeeRef });
  };

  // `follow-up-detail-page.tsx` renders this only while `composing`, so the whole Radix root
  // mounts in the open state and Radix recorded no element to come back to. First render is
  // the moment it opened, which is why `true` is the honest argument here.
  const { onCloseAutoFocus } = useReturnFocusToOpener(true);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          {/* `MEM-037` AC-2, and the prototype's own string. */}
          <DialogTitle>{composerTitle(item)}</DialogTitle>
          {subtitle === null ? null : <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>

        <section className="space-y-2">
          <p className="text-2xs uppercase tracking-wider text-muted-foreground">
            {OPENER_PICKER_LABEL}
          </p>
          <ul className="space-y-2">
            {item.openers.map((opener) => (
              <li key={opener}>
                <SelectableCard
                  control="radio"
                  name="follow-up-opener"
                  selected={draft === opener}
                  onSelect={() => setDraft(opener)}
                  label={opener}
                />
              </li>
            ))}
            <li>
              <SelectableCard
                control="radio"
                name="follow-up-opener"
                selected={draft.length === 0}
                onSelect={() => setDraft('')}
                label={START_FROM_SCRATCH_LABEL}
              />
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <label
            className="text-2xs uppercase tracking-wider text-muted-foreground"
            htmlFor="follow-up-draft"
          >
            {DRAFT_LABEL}
          </label>
          <Textarea
            id="follow-up-draft"
            value={draft}
            rows={6}
            onChange={(event) => {
              setDraft(event.target.value);
              setCopied(false);
            }}
          />
        </section>

        {/*
          `MEM-038` AC-2 and AC-6. Channels when they published one; a plain sentence when they did
          not — and the draft above stays copyable either way, which is AC-6's whole point.
        */}
        <section className="space-y-2">
          <p className="text-2xs uppercase tracking-wider text-muted-foreground">
            {CHANNELS_LABEL}
          </p>
          {item.channels.length === 0 ? (
            <p className="text-xs text-muted-foreground">{NO_CHANNEL_NOTICE}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {item.channels.map((channel) => (
                <li key={channel.url}>
                  <a
                    href={channel.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1.5 rounded-pill border px-3 py-1.5 text-xs"
                  >
                    {channel.label}
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {copied ? <p className="text-xs text-success">{COPIED_NOTICE}</p> : null}

        <div className="flex items-center gap-2">
          <Button
            className="flex-1"
            disabled={draft.trim().length === 0 || copy.isPending}
            onClick={() => void onCopy()}
          >
            <Copy className="mr-1.5 h-4 w-4" aria-hidden />
            {copied ? COPIED_LABEL : COPY_OPENER_LABEL}
          </Button>
          <Button
            variant="outline"
            title={MARK_DONE_DESCRIPTION}
            aria-label={`${MARK_DONE_LABEL} — ${MARK_DONE_DESCRIPTION}`}
            disabled={markDone.isPending}
            onClick={() => {
              markDone.mutate({ eventId, attendeeRef: item.attendeeRef });
              onClose();
            }}
          >
            {MARK_DONE_LABEL}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
